import React, { useCallback, useMemo } from "react";

import { faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";

import IconButton from "~/ui/legacy/IconButton";
import Selector, { type SingleValue } from "~/ui/selectors/Selector";

import { useDesktopSetting } from "../../DesktopAppSettings";

import styles from "./BotlessRecordingDisabledApps.module.css";

// TODO: I need to verify the bundle identifiers for some of these apps still
const AVAILABLE_OPTIONS = [
	{
		label: "Chrome",
		value: "chrome",
	},
	{
		label: "Firefox",
		value: "firefox",
	},
	{
		label: "Safari",
		value: "safari",
	},
	{
		label: "Opera",
		value: "opera",
	},
	{
		label: "Zoom",
		value: "zoom",
	},
	{
		label: "Microsoft Teams",
		value: "teams",
	},
	{
		label: "WhatsApp",
		value: "whatsapp",
	},
	{
		label: "Facetime",
		value: "facetime",
	},
	{
		label: "Webex",
		value: "webex",
	},
	{
		label: "Slack",
		value: "slack",
	},
	{
		label: "Discord",
		value: "discord",
	},
];

const VALUE_TO_LABELS = AVAILABLE_OPTIONS.reduce(
	(acc, option) => {
		acc[option.value] = option.label;
		return acc;
	},
	{} as { [key: string]: string },
);

function BlockedApp({ onUnblockApp, app }: { onUnblockApp: (app: string) => void; app: string }) {
	const onClick = useCallback(() => {
		onUnblockApp(app);
	}, [app, onUnblockApp]);

	// If the app is not in the VALUE_TO_LABELS, we will just show the raw app name
	return (
		<div className={styles.blockedApp}>
			{VALUE_TO_LABELS[app] ?? app}
			<IconButton icon={faTimes} onClick={onClick} />
		</div>
	);
}

export default function BotlessRecordingDisabledApps() {
	const botlessRecorderSettings = useDesktopSetting("botlessRecorder");
	const disabledApps = useMemo(() => {
		// Electron doesn't support Sets for their store API. So we need to convert it here
		// We don't necessarily need to do so but this is more performant and easier to work with.
		return new Set(botlessRecorderSettings ? botlessRecorderSettings.value.disabledApps : []);
	}, [botlessRecorderSettings]);

	const onSelect = useCallback(
		(selected: SingleValue<{ label: string; value: string }>) => {
			if (!selected || !botlessRecorderSettings) return;
			const addedToDisabledApps = selected.value;
			const newDisabledApps = [...disabledApps, addedToDisabledApps];
			// Removing potential duplicates in case something goes wrong
			const uniqueDisabledApps = [...new Set(newDisabledApps)];
			botlessRecorderSettings.update({
				autoDetectEnabled: botlessRecorderSettings.value.autoDetectEnabled,
				disabledApps: uniqueDisabledApps,
			});
		},
		[botlessRecorderSettings, disabledApps],
	);

	const options = useMemo(() => {
		return AVAILABLE_OPTIONS.filter(app => {
			return !disabledApps.has(app.value);
		});
	}, [disabledApps]);

	const onUnblockApp = useCallback(
		(app: string) => {
			if (!botlessRecorderSettings) return;
			const newDisabledApps = new Set(disabledApps);
			newDisabledApps.delete(app);
			botlessRecorderSettings.update({
				autoDetectEnabled: botlessRecorderSettings.value.autoDetectEnabled,
				disabledApps: Array.from(newDisabledApps),
			});
		},
		[botlessRecorderSettings, disabledApps],
	);

	return (
		<div className={styles.container}>
			<div className={styles.header}>
				Mute notifications for these apps:
				<Selector condensed onChange={onSelect} options={options} controlShouldRenderValue={false} />
			</div>
			<div className={styles.blockedApps}>
				{disabledApps.size === 0 && <p className={styles.noAppsMuted}>No apps muted</p>}
				{Array.from(disabledApps).map(app => (
					<BlockedApp key={app} onUnblockApp={onUnblockApp} app={app} />
				))}
			</div>
		</div>
	);
}
